import React from "react";

import { faUserCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { choiceModal } from "~/lib/choiceModal";
import { dispatchAnalyticsTrack } from "~/lib/segment";

import useEventAttendanceMutation from "../hooks/useEventAttendanceMutation";
import type { RowProps } from "./index";

import type { EventEditType, ResponseStatusChoices } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";

import styles from "./ViewEvent.module.css";

type Props = RowProps & {
	onAnsweredRsvp?: () => void;
};

export default function RSVPRow({ event, eventData, onAnsweredRsvp }: Props) {
	const currentResponse = eventData.userResponseStatus || "";
	const [mutationLoading, setMutationLoading] = React.useState(false);
	const eventAttendance = useEventAttendanceMutation();

	const rsvp = React.useCallback(
		(response: ResponseStatusChoices) => async () => {
			if (mutationLoading) return;

			dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "rsvp", value: response });

			if (eventData.recurrenceRule && eventData.calendar.source !== "office365") {
				// Then it's occurrence, need to ask for edit type
				const choices: { value: EventEditType; label: string }[] = [
					{ value: "THIS", label: "This event" },
					{ value: "ALL", label: "All events" },
				];
				const result = await choiceModal({ choices, title: "RSVP to recurring event" });
				if (result) {
					await eventAttendance({
						calendarId: eventData.calendar.id,
						eventId: eventData.id,
						eventGuid: event.guid,
						eventTitle: event.title,
						userResponseStatus: response,
						editType: result.value as EventEditType,
						setMutationLoading,
						onAnsweredRsvp,
					});
				}
			} else {
				await eventAttendance({
					calendarId: eventData.calendar.id,
					eventId: eventData.id,
					eventGuid: event.guid,
					eventTitle: event.title,
					userResponseStatus: response,
					editType: "THIS",
					setMutationLoading,
					onAnsweredRsvp,
				});
			}
		},
		[
			mutationLoading,
			eventData.recurrenceRule,
			eventData.calendar.source,
			eventData.calendar.id,
			eventData.id,
			eventAttendance,
			event.guid,
			event.title,
			onAnsweredRsvp,
		],
	);

	return (
		<div className={styles.infoRow}>
			<div className={cx(styles.infoRow__icon, styles.centered, styles.alignLeft)}>
				<FontAwesomeIcon fixedWidth icon={faUserCheck} />
			</div>
			<div className={styles.infoRow__text}>
				<div className={styles.rsvp}>
					<div className={styles.rsvp__label}>Going?</div>
					<div
						className={cx(styles.rsvp__button, { [styles.active]: currentResponse === "accepted" })}
						onClick={rsvp("accepted")}
					>
						Yes
					</div>
					<div
						className={cx(styles.rsvp__button, { [styles.active]: currentResponse === "declined" })}
						onClick={rsvp("declined")}
					>
						No
					</div>
					<div
						className={cx(styles.rsvp__button, {
							[styles.active]: ["tentative", "tentativelyAccepted"].includes(currentResponse),
						})}
						onClick={rsvp("tentative")}
					>
						Maybe
					</div>
				</div>
			</div>
		</div>
	);
}
